class SiteSearch {
  constructor() {
    this.elements = {};
    this.searchIndex = [];
    this.maxResults = 10;
    this.searchIndexLoaded = false;
    this.searchIndexPromise = null;
    this.debouncedSearch = this.debounce(this.performSearch.bind(this), 300);
    this.resultItemTemplate = null;
  }

  async init() {
    // Get DOM elements only once
    this.cacheElements();
    
    // Start loading search index immediately
    this.preloadSearchIndex();
    
    // Set up event listeners with event delegation where possible
    this.setupEventListeners();
    
    // Only run these if we have the required elements
    if (this.elements.form && this.elements.input) {
      this.handleInitialSearch();
      this.showInitialProducts();
    }
  }

  cacheElements() {
    // Cache all DOM elements in one go to avoid repeated DOM lookups
    this.elements = {
      masthead: document.getElementById('masthead'),
      toggle: document.getElementById('site-search-toggle'),
      form: document.getElementById('site-search-form'),
      input: document.getElementById('search-input'),
      results: document.getElementById('search-results'),
      resultsTitle: document.getElementById('search-results-title')
    };

    // Only query facets if needed to avoid unnecessary DOM traversal
    const facetsContainer = document.getElementById('search-facets');
    if (facetsContainer) {
      this.elements.facets = facetsContainer.getElementsByTagName('li');
    }
  }

  preloadSearchIndex() {
    // Store promise for future use and avoid multiple fetches
    if (!this.searchIndexPromise) {
      this.searchIndexPromise = fetch('/search-index.json')
        .then(response => {
          if (!response.ok) {
            throw new Error('Network response was not ok');
          }
          return response.json();
        })
        .then(data => {
          this.searchIndex = data;
          this.searchIndexLoaded = true;
          return data;
        })
        .catch(error => {
          console.error('Error loading search index:', error);
          return [];
        });
    }
    return this.searchIndexPromise;
  }

  setupEventListeners() {
    // Only set up listeners if elements exist
    if (this.elements.toggle) {
      this.elements.toggle.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggleSearchForm();
      });
      
      // Add ARIA attributes for accessibility
      this.elements.toggle.setAttribute('aria-expanded', 'false');
      this.elements.toggle.setAttribute('aria-controls', 'site-search-form');
    }

    // Event delegation for facets
    if (this.elements.facets) {
      const facetsContainer = document.getElementById('search-facets');
      facetsContainer.addEventListener('click', (e) => {
        // Only process clicks on links
        if (e.target.tagName === 'A') {
          e.preventDefault();
          this.handleSearchPresets(e, e.target);
        }
      });
    }

    // Input event with debounce to prevent excessive searches
    if (this.elements.input) {
      this.elements.input.addEventListener('input', () => {
        this.debouncedSearch();
      });
      
      // Add keydown event for accessibility (Escape key)
      this.elements.input.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.closeSearchForm();
          e.preventDefault(); // Prevent default browser behavior
        }
      });
    }
    
    // Set up focus management for WCAG compliance
    if (this.elements.form) {
      // Use focusin/focusout instead of focus/blur for better event bubbling
      this.elements.form.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.closeSearchForm();
          e.preventDefault();
        }
      });
      
      // Set up a focus trap for the search form
      this.setupFocusTrap();
      
      // Handle form losing focus (close search when focus moves outside the form)
      document.addEventListener('focusin', (e) => {
        // Check if the form is open and the new focus target is outside the form
        if (!this.elements.form.classList.contains('hidden') &&
            !this.elements.form.contains(e.target) && 
            e.target !== this.elements.toggle) {
          this.closeSearchForm();
        }
      });
    }
    
    // Add announcement for screen readers when results update
    if (this.elements.results) {
      this.setupLiveRegion();
    }
  }
  
  setupLiveRegion() {
    // Create a live region for screen reader announcements
    this.elements.liveRegion = document.createElement('div');
    this.elements.liveRegion.setAttribute('aria-live', 'polite');
    this.elements.liveRegion.setAttribute('aria-atomic', 'true');
    this.elements.liveRegion.classList.add('sr-only'); // Add your screen-reader-only class
    
    // Append to the DOM
    document.body.appendChild(this.elements.liveRegion);
  }
  
  setupFocusTrap() {
    // Find all focusable elements within the form
    this.updateFocusableElements();
    
    // Add keydown listener for tab key to manage focus
    this.elements.form.addEventListener('keydown', (e) => {
      if (e.key === 'Tab') {
        // Update the list in case DOM has changed
        this.updateFocusableElements();
        
        if (this.focusableElements.length === 0) return;
        
        // Handle tab trapping
        const firstElement = this.focusableElements[0];
        const lastElement = this.focusableElements[this.focusableElements.length - 1];
        
        if (e.shiftKey && document.activeElement === firstElement) {
          // Shift+Tab on first element moves to last element
          lastElement.focus();
          e.preventDefault();
        } else if (!e.shiftKey && document.activeElement === lastElement) {
          // Tab on last element moves to first element
          firstElement.focus();
          e.preventDefault();
        }
      }
    });
  }
  
  updateFocusableElements() {
    // Get all focusable elements within the form
    this.focusableElements = Array.from(
      this.elements.form.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
    ).filter(el => !el.disabled && el.offsetParent !== null);
  }

  toggleSearchForm() {
    const { form, input, toggle } = this.elements;
    if (!form) return;
    
    const isHidden = form.classList.contains('hidden');
    
    if (isHidden) {
      this.openSearchForm();
    } else {
      this.closeSearchForm();
    }
  }
  
  openSearchForm() {
    const { form, input, toggle, masthead } = this.elements;
    if (!form) return;
    
    // Update ARIA attributes
    toggle.setAttribute('aria-expanded', 'true');
    
    // Show the form
    form.classList.remove('hidden');
    masthead.classList.add('search--open');
    
    // Focus the input
    if (input) {
      input.focus();
    }
    
    // Announce for screen readers
    this.announceToScreenReader('Search form opened');
  }
  
  closeSearchForm() {
    const { form, toggle, masthead } = this.elements;
    if (!form || form.classList.contains('hidden')) return;
    
    // Update ARIA attributes
    toggle.setAttribute('aria-expanded', 'false');
    
    // Hide the form
    form.classList.add('hidden');
    masthead.classList.remove('search--open');
    
    // Return focus to the toggle button
    if (toggle) {
      toggle.focus();
    }
    
    // Announce for screen readers
    this.announceToScreenReader('Search form closed');
  }
  
  announceToScreenReader(message) {
    if (this.elements.liveRegion) {
      this.elements.liveRegion.textContent = message;
    }
  }

  handleSearchPresets(e, link) {
    const terms = decodeURIComponent(link.href.split('=')[1]);
    this.setQueryParam('search', terms);
    
    if (this.elements.input) {
      this.elements.input.value = terms;
      this.elements.input.focus();
      this.performSearch();
    }
  }

  async performSearch() {
    const query = this.elements.input.value.trim().toLowerCase();
    
    // Clear results with a single operation
    if (this.elements.results) {
      this.elements.results.innerHTML = '';
    }
    
    if (query.length === 0) {
      this.showInitialProducts();
      return;
    }

    // Ensure search index is loaded before searching
    if (!this.searchIndexLoaded) {
      await this.preloadSearchIndex();
    }

    const searchTerms = query.split(' ').filter(term => term.length > 0);
    
    // Use more efficient filtering
    const filteredResults = this.searchIndex.filter(item => {
      if (item.type !== 'product') return false;
      
      const title = item.title.toLowerCase();
      
      // Quick check for exact match
      if (title.includes(query)) return true;
      
      // Check individual terms
      for (const term of searchTerms) {
        if (title.includes(term)) return true;
        
        // Check categories (but avoid deep nesting)
        for (const cat of item.categories) {
          if (cat.name.toLowerCase().includes(term)) return true;
        }
      }
      
      return false;
    });

    this.renderResults(filteredResults);
    
    // Announce results to screen readers
    this.announceSearchResults(filteredResults.length);
  }
  
  announceSearchResults(count) {
    let message;
    if (count === 0) {
      message = 'No search results found.';
    } else if (count === 1) {
      message = '1 search result found.';
    } else {
      message = `${count} search results found.`;
    }
    
    this.announceToScreenReader(message);
  }

  renderResults(results) {
    const { results: container, resultsTitle } = this.elements;
    if (!container) return;
    
    if (resultsTitle) {
      resultsTitle.textContent = 'Search Results';
    }

    if (results.length === 0) {
      container.innerHTML = '<li>No results found</li>';
      if (resultsTitle) {
        resultsTitle.textContent = 'Popular Products';
      }
      return;
    }

    // Use DocumentFragment for better performance with multiple DOM updates
    const fragment = document.createDocumentFragment();
    
    results.slice(0, this.maxResults).forEach((result, index) => {
      const listItem = this.createResultItem(result, index);
      fragment.appendChild(listItem);
    });
    
    // Single DOM operation to add all results
    container.appendChild(fragment);
    
    // Make sure container has proper ARIA attributes
    container.setAttribute('role', 'listbox');
    container.setAttribute('aria-label', 'Search results');
  }

  createResultItem(result, index) {
    // Create element once
    const listItem = document.createElement('li');
    listItem.classList.add('site-search-results-item');
    listItem.setAttribute('role', 'option');
    listItem.setAttribute('aria-posinset', index + 1);
    
    // Avoid unnecessary string concatenation with template literals
    listItem.innerHTML = `
      <a class="site-search-results-link" href="${result.url}">
        <img 
          class="site-search-results-img" 
          src="${result.images[0].customImages.thumbnailSearch.url}" 
          width="112"
          height="112"
          alt="${result.title}" 
          loading="lazy"
        />
        <div class="site-search-results-txt">
          <h4 class="site-search-results-title">${result.title}</h4>
          <p class="site-search-results-categories">
            ${result.categories[0].name}
          </p>
          <p class="site-search-results-btn">Shop Now</p>
        </div>
      </a>
    `;

    return listItem;
  }

  getQueryParam(key) {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(key);
  }

  setQueryParam(key, value) {
    const url = new URL(window.location.href);
    url.searchParams.set(key, value);
    window.history.replaceState(null, '', url.toString());
  }

  async showInitialProducts() {
    if (!this.elements.results) return;
    
    // Ensure search index is loaded
    if (!this.searchIndexLoaded) {
      await this.preloadSearchIndex();
    }
    
    const initialProducts = this.searchIndex
      .filter(item => item.type === 'product')
      .slice(0, 3);
    
    this.renderResults(initialProducts);
    
    if (this.elements.resultsTitle) {
      this.elements.resultsTitle.textContent = 'Popular Products';
    }
  }

  handleInitialSearch() {
    const searchParam = this.getQueryParam('search');
    if (searchParam && this.elements.form && this.elements.input) {
      this.elements.form.classList.remove('hidden');
      if (this.elements.toggle) {
        this.elements.toggle.setAttribute('aria-expanded', 'true');
      }
      this.elements.input.value = searchParam;
      this.elements.input.focus();
      this.performSearch();
    }
  }

  // Utility function for debouncing search input
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}

export default {
  init() {
    const siteSearch = new SiteSearch();
    
    // Wait for DOM to be ready before initializing
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => siteSearch.init());
    } else {
      siteSearch.init();
    }
  }
};